import { MbscCalendarEvent, MbscCalendarEventData } from '@mobiscroll/react';
import React from 'react';
import { IconButton } from '@mui/material';
import { ContentCopy, Delete, Edit } from '@mui/icons-material';
import {
	EmployeeStatusIndicator,
	EmployeeStatusInfo,
} from '../../components/EmployeeStatusIndicator';
import moment from 'moment';
import type { ScheduleResource as ScheduleResourceType } from 'types/scheduler';
import {
	isEmployeeResource,
	isOpenShiftResource,
	isJobCodeResource,
	isEventTypeResource,
} from 'types/scheduler';

interface ScheduleEventProps {
	calendarType: string;
	calendarView?: string; // 'day', 'week', or 'workweek'
	data: MbscCalendarEventData;
	onEditClick?: (event: MbscCalendarEvent) => void;
	onDeleteClick?: (event: MbscCalendarEvent) => void;
	onCopyClick?: (event: MbscCalendarEvent) => void;
}

/**
 * Calculate the relative luminance of a color
 * Based on WCAG guidelines for color contrast
 */
const getColorLuminance = (hexColor: string): number => {
	// Remove # if present
	const hex = hexColor.replace('#', '');

	// Convert to RGB
	const r = parseInt(hex.substr(0, 2), 16) / 255;
	const g = parseInt(hex.substr(2, 2), 16) / 255;
	const b = parseInt(hex.substr(4, 2), 16) / 255;

	// Apply gamma correction
	const rLinear = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
	const gLinear = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
	const bLinear = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

	// Calculate relative luminance
	return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Determine if a background color is light or dark
 * Returns true if the color is light (needs dark text)
 */
const isLightColor = (hexColor: string): boolean => {
	if (!hexColor || hexColor === 'transparent') return false;
	try {
		const luminance = getColorLuminance(hexColor);
		// Threshold of 0.5 works well for most cases
		return luminance > 0.5;
	} catch (e) {
		// If there's an error parsing the color, default to white text
		return false;
	}
};

const getScheduleEventStyles = (original: MbscCalendarEvent, calendarView?: string) => {
	// Check if this is a time off event
	const isTimeOffEvent = (original as any).isTimeOff;

	// Special styling for time off events in week view
	if (isTimeOffEvent && (calendarView === 'week' || calendarView === 'workweek')) {
		return {
			cursor: 'default',
			backgroundColor: '#FF9800',
			background: 'repeating-linear-gradient(45deg, #FF9800, #FF9800 10px, #FFB74D 10px, #FFB74D 20px)',
			color: '#ffffff',
			borderColor: '#F57C00',
			border: '2px solid #F57C00',
			fontWeight: 600,
			opacity: 0.9,
		};
	}

	const dynamicStyle = original.added_by_mabel
		? { border: '2px solid var(--color-purple)' }
		: { borderColor: original?.background_color };

	// Determine text color based on background color brightness
	const backgroundColor = original?.background_color || '#446388';
	const textColor = isLightColor(backgroundColor) ? '#000000' : '#ffffff';

	return {
		cursor: 'pointer',
		backgroundColor: backgroundColor,
		color: textColor,
		...dynamicStyle,
	};
};

const getEventClassNames = (calendarType: string, original: MbscCalendarEventData['original']) => {
	return [
		'md-timeline-template-event',
		original?.overlap ? 'hasStripeBackground' : '',
		calendarType === 'request' ? 'requestsEventCustom' : '',
		'scheduleEvent',
		original?.role === undefined && calendarType === 'request' ? 'timeoffColor' : '',
	]
		.filter(Boolean)
		.join(' ');
};

export const ScheduleEvent: React.FC<ScheduleEventProps> = ({
	calendarType,
	calendarView,
	data,
	onEditClick,
	onDeleteClick,
	onCopyClick,
}) => {
	const { original } = data!;
	// console.log('ScheduleEvent original data:', original, data);

	const isRequest = original.role !== undefined || calendarType !== 'request';

	if (original?.shift_type?.id === 2 && original?.scheduled) return null;

	const handleEditClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (onEditClick && original) {
			onEditClick(original);
		}
	};

	const handleDeleteClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (onDeleteClick && original) {
			onDeleteClick(original);
		}
	};

	const handleCopyClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (onCopyClick && original) {
			onCopyClick(original);
		}
	};

	// Build employee status array for events
	const employeeStatuses: EmployeeStatusInfo[] = [];

	// Check for time-off conflicts using employeeTimeOff data (specific date ranges)
	if (original?.employee && original?.startAt && original?.endAt) {
		const shiftStart = moment(original.startAt);

		const employeeTimeOff = (original as any).employeeTimeOff;
		if (employeeTimeOff && employeeTimeOff.length > 0) {
			const shiftDate = shiftStart.format('YYYY-MM-DD');

			const matchingTimeOff = employeeTimeOff.find((timeOff: any) => {
				if (timeOff.startAt && timeOff.endAt) {
					// Extract only date parts for comparison (ignore time)
					const timeOffStartDate = moment(timeOff.startAt).format('YYYY-MM-DD');
					const timeOffEndDate = moment(timeOff.endAt).format('YYYY-MM-DD');

					// Check if shift date falls within time-off period (inclusive)
					return shiftDate >= timeOffStartDate && shiftDate <= timeOffEndDate;
				}
				return false;
			});

			if (matchingTimeOff) {
				const timeOffStart = moment(matchingTimeOff.startAt).format('MMM D, YYYY');
				const timeOffEnd = moment(matchingTimeOff.endAt).format('MMM D, YYYY');
				const timeOffMessage =
					timeOffStart === timeOffEnd
						? `Employee has time off on ${timeOffStart}`
						: `Employee has time off: ${timeOffStart} - ${timeOffEnd}`;

				employeeStatuses.push({
					type: 'time_off',
					label: 'Time Off Scheduled',
					details: timeOffMessage,
					severity: 'error',
				});
			}
		}
	}
	// console.log('Employee statuses after availability/time-off check:', original?.employeeStatuses, employeeStatuses);

	// Include other statuses from employeeStatusData if available
	if (original?.employeeStatusData) {
		const statusData = original.employeeStatusData;
		
		// console.log('ScheduleEvent statusData:', {
		// 	shiftId: original.id,
		// 	employee: original.employee,
		// 	hasAvailabilityIssue: statusData.hasAvailabilityIssue,
		// 	availabilityMessage: statusData.availabilityMessage,
		// });

		// Check for availability conflicts from statusData (calculated in SchedulePage)
		if (statusData.hasAvailabilityIssue) {
			employeeStatuses.push({
				type: 'not_available',
				label: 'Availability Conflict',
				details: statusData.availabilityMessage || 'Not available for this shift',
				severity: 'error',
			});
		}

		// Show daily overtime statuses in both day and week views
		if (statusData.isShiftCausingDailyOT) {
			const overtimeHours = statusData.todayOvertimeHours;
			employeeStatuses.push({
				type: 'in_overtime',
				label: 'In Daily Overtime',
				details: `This shift pushes employee over daily threshold with a total of ${overtimeHours.toFixed(1)} hours in overtime today`,
				severity: 'error',
			});
		} else if (statusData.isApproachingDailyOvertime && statusData.dailyOTWarning) {
			// Only show if dailyOTWarning setting is configured
			employeeStatuses.push({
				type: 'approaching_overtime',
				label: 'Approaching Daily Overtime',
				details: `${statusData.todayHours.toFixed(1)} hours today (warning at ${statusData.dailyOTWarning} hours)`,
				severity: 'warning',
			});
		}

		// Week view: show weekly overtime statuses
		if (calendarView === 'week' || calendarView === 'workweek') {
			if (statusData.isShiftCausingWeeklyOT) {
				const weeklyOvertimeHours = statusData.weeklyOvertimeHours;
				employeeStatuses.push({
					type: 'in_overtime',
					label: 'In Weekly Overtime',
					details: `This shift pushes employee over weekly threshold with a total of ${weeklyOvertimeHours.toFixed(1)} hours in overtime this week`,
					severity: 'error',
				});
			} else if (statusData.isShiftCausingWeeklyApproach && statusData.weeklyOTWarning) {
				// Only show on the shift that triggers the weekly overtime warning
				employeeStatuses.push({
					type: 'approaching_overtime',
					label: 'Approaching Weekly Overtime',
					details: `${statusData.totalHours.toFixed(1)} hours this week (warning at ${statusData.weeklyOTWarning} hours)`,
					severity: 'warning',
				});
			}
			if (statusData.isMinor) {
				employeeStatuses.push({
					type: 'minor',
					label: 'Minor Employee',
					details: 'Employee is under the age of minor',
					severity: 'info',
				});
			}
		}
	}

	// console.log('ScheduleEvent final employeeStatuses:', {
	// 	shiftId: original.id,
	// 	employeeName: original?.employee ? `${original.employee.firstName} ${original.employee.lastName}` : 'House Shift',
	// 	statusCount: employeeStatuses.length,
	// 	statuses: employeeStatuses,
	// });

	// Check if this is a time off event
	const isTimeOffEvent = (original as any).isTimeOff;
	const isWeekView = calendarView === 'week' || calendarView === 'workweek';

	return (
		<div
			className={getEventClassNames(calendarType, original)}
			style={getScheduleEventStyles(original, calendarView)}
		>
			<div className="md-custom-event-wrapper">
				<div className="md-custom-event-details">
					<div className="md-custom-event-header">
						<div className="md-custom-event-title">
							{isTimeOffEvent && isWeekView ? (
								<div className="shift-info" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
									<span style={{ fontSize: '1.2rem' }}>🏖️</span>
									<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
										<div style={{ fontWeight: 700, fontSize: '0.95rem' }}>TIME OFF</div>
										<div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
											{moment(original.startAt).format('MMM D')} - {moment(original.endAt).format('MMM D, YYYY')}
										</div>
									</div>
								</div>
							) : isRequest ? (
								<div className="shift-info">
									<div
										className="shift-time"
										style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
									>
										<span>
										{moment(original.startAt).format('hh:mm a')} - {moment(original.endAt).format('hh:mm a')}
										</span>
										{!original?.published && (
											<div
												style={{
													fontSize: '0.75rem',
													color: '#ffc107',
													fontWeight: 500,
													display: 'flex',
													alignItems: 'center',
													gap: '4px',
												}}
											>
												<span style={{ fontSize: '0.9rem' }}>●</span>
												Unpublished
											</div>
										)}
									</div>
									<div className="employee-name">
										{original?.employee
											? `${original.employee.firstName} ${original.employee.lastName}`
											: 'House Shift'}
										{employeeStatuses.length > 0 && (
											<EmployeeStatusIndicator
												statuses={employeeStatuses}
												employeeName={
													original?.employee
														? `${original.employee.firstName} ${original.employee.lastName}`
														: undefined
												}
											/>
										)}
									</div>
									{original?.jobCode && (
										<div className="job-code">
											{original.jobCode.jobNumber
												? `${original.jobCode.jobNumber} - ${original.jobCode.label}`
												: original.jobCode.label}
										</div>
									)}
								</div>
							) : (
								'Time off'
							)}
						</div>
						{!isTimeOffEvent && (onEditClick || onDeleteClick || onCopyClick) && (
							<div className="action-buttons">
								{onEditClick && (
									<IconButton
										onClick={handleEditClick}
										size="small"
										style={{
											backgroundColor: 'white',
											marginRight: '4px',
											color: 'rgb(68, 99, 136)',
										}}
										className="edit-btn"
									>
										<Edit fontSize="small" />
									</IconButton>
								)}
								{onCopyClick && (
									<IconButton
										onClick={handleCopyClick}
										size="small"
										style={{
											backgroundColor: 'white',
											marginRight: '4px',
											color: 'rgb(68, 99, 136)',
										}}
										className="copy-btn"
									>
										<ContentCopy fontSize="small" />
									</IconButton>
								)}
								{onDeleteClick && (
									<IconButton
										onClick={handleDeleteClick}
										size="small"
										style={{ backgroundColor: 'white', color: '#d32f2f' }}
										className="delete-btn"
									>
										<Delete fontSize="small" />
									</IconButton>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

interface ScheduleResourceProps {
	resource: ScheduleResourceType;
	calendarType: string;
}

export const ScheduleResourceComponent: React.FC<ScheduleResourceProps> = ({
	resource,
	calendarType: _calendarType,
}) => {
	// Use type guards to determine resource type
	const isEmployee = isEmployeeResource(resource);
	const isOpenShift = isOpenShiftResource(resource);
	const _isJobCode = isJobCodeResource(resource);
	const _isEventType = isEventTypeResource(resource);

	// Build status array for employee resources
	const employeeStatuses: EmployeeStatusInfo[] = [];
	if (isEmployee) {
		// TypeScript now knows resource is EmployeeResource
		// Check for overtime
		if (resource.hasOvertime) {
			if (resource.hasWeeklyOvertime) {
				// Always use overtimeHours which already has the correct calculation (hours - threshold)
				const overtimeHours = resource.weeklyOvertimeHours ?? 0;
				employeeStatuses.push({
					type: 'in_overtime',
					label: `In Weekly Overtime`,
					details: `Working ${overtimeHours.toFixed(1)} hours over Weekly threshold`,
					severity: 'error',
				});
			}
			else if (resource.hasDailyOvertime) {
				// Use totalDailyOvertimeHours to show total OT across all days in the week
				const overtimeHours = resource.totalDailyOvertimeHours ?? resource.overtimeHours ?? 0;
				employeeStatuses.push({
					type: 'in_overtime',
					label: `In Daily Overtime`,
					details: `Working ${overtimeHours.toFixed(1)} hours over Daily threshold`,
					severity: 'error',
				});
			}
			
		} else if (resource.isApproachingWeeklyOvertime && resource.weeklyOTWarning) {
			// Week view: only show approaching weekly overtime if setting is configured
			employeeStatuses.push({
				type: 'approaching_overtime',
				label: 'Approaching Weekly Overtime',
				details: `${resource.totalHours.toFixed(1)} hours this week (warning at ${resource.weeklyOTWarning} hours)`,
				severity: 'warning',
			});
		}

		// Check for time off
		if (resource.hasTimeOff) {
			employeeStatuses.push({
				type: 'time_off',
				label: 'Time Off Scheduled',
				details: 'Employee has approved time off during this week',
				severity: 'error',
			});
		}

		// Check for availability issues
		if (resource.hasAvailabilityIssue) {
			employeeStatuses.push({
				type: 'not_available',
				label: 'Availability Conflict',
				details: 'One or more shifts conflict with employee availability',
				severity: 'error',
			});
		}

		// Check for minor
		if (resource.isMinor) {
			employeeStatuses.push({
				type: 'minor',
				label: 'Minor Employee',
				details: 'Employee is under the age of minor',
				severity: 'info',
			});
		}
	}
	// Get display name based on resource type
	const displayName = isEmployee || isOpenShift ? (resource as any).displayName : resource.name;

	const totalHours =
		(isEmployee || isOpenShift) && 'totalHours' in resource
			? (resource as any).totalHours
			: undefined;

	return (
		<div className={`md-work-week-cont`}>
			<div className="md-work-week-name">
				{isEmployee || isOpenShift ? (
					// Week view: Show employee name with total hours
					<div className="employee-resource">
						<div className="employee-name" style={{ display: 'flex', alignItems: 'center' }}>
							<span>{displayName}</span>
							{employeeStatuses.length > 0 && (
								<EmployeeStatusIndicator statuses={employeeStatuses} employeeName={displayName} />
							)}
						</div>
						{totalHours !== undefined && (
							<div className="employee-hours">{totalHours.toFixed(1)} hours</div>
						)}
					</div>
				) : (
					// Day view: Show job code (existing logic)
					<div className="jobcode-resource">{resource.name}</div>
				)}
			</div>
		</div>
	);
};

// Export with backward compatible name
export const ScheduleResource = ScheduleResourceComponent;

export default ScheduleEvent;
